import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import FolderIcon from '@mui/icons-material/FolderOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import WorkIcon from '@mui/icons-material/WorkOutline';
import { Box, Link, Stack, Typography } from '@mui/material';
import { ENGLISH_LEVEL_SHORT_OPTIONS } from 'domain/shared/core/constants';
import { EMPLOYEE_CONTRACT_ASSIGMENT_TYPES, EmployeeLinkWithNameLoading } from 'domain/shared/employee';
import { ObjectDetailsSections } from 'framework/ViewTypes/embeddable';
import { DATE_FORMAT, EMPTY_VALUE_INDICATOR, SanitizedHtml, useDateService } from 'framework/utils';
import { Link as RouterLink } from 'react-router-dom';
import { POSITION_SLOTS_PATH, POSITION_SLOT_WORKER_TYPE_LABELS } from 'domain/shared/positionSlot';
import { AppIconLabel } from 'framework/ui/mui-builder';
import { DetailField, SimpleLinkField } from 'framework/ViewTypes';
import type { PositionFillRequestListItem } from '../../types';
import { useRecruitmentPoolUrl } from 'domain/recruiting/recruitmentPool/routing';

type PositionFillRequestDetailsCardProps = {
  positionFillRequest: PositionFillRequestListItem;
};

const htmlContentSx = {
  '& > :first-of-type': { mt: 0 },
  '& > :last-child': { mb: 0 },
};

const PositionFillRequestDetailsCard = ({ positionFillRequest }: PositionFillRequestDetailsCardProps) => {
  const { getSingleEntityUrl: getRecruitmentPoolUrl } = useRecruitmentPoolUrl();
  const { formatDate } = useDateService();
  const expirationDate = formatDate(positionFillRequest.expirationDate, DATE_FORMAT.SIMPLE)?.trim();
  const position = positionFillRequest.positionSlot ? (
    <Link
      component={RouterLink}
      to={`${POSITION_SLOTS_PATH}/preview/${positionFillRequest.positionSlotId}`}
      underline="hover"
    >
      {positionFillRequest.requestDisplayName}
    </Link>
  ) : (
    EMPTY_VALUE_INDICATOR
  );

  return (
    <ObjectDetailsSections
      sections={[
        {
          title: 'Request details',
          content: (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
              <DetailField
                icon={<PersonOutlineOutlinedIcon />}
                tooltip="Owner"
                value={
                  <EmployeeLinkWithNameLoading
                    id={positionFillRequest.ownerAlias}
                    defaultValue={positionFillRequest.ownerAlias}
                  />
                }
              />
              <DetailField icon={<HourglassEmptyOutlinedIcon />} tooltip="Expiration date" value={expirationDate} />
              {positionFillRequest.recruitmentPoolId && (
                <DetailField
                  icon={<AppIconLabel icon="three-dots" />}
                  tooltip="Recruitment pool"
                  value={
                    <SimpleLinkField
                      to={getRecruitmentPoolUrl(positionFillRequest.recruitmentPoolId)}
                      value={positionFillRequest?.recruitmentPoolName ?? positionFillRequest.recruitmentPoolId}
                    />
                  }
                />
              )}
            </Box>
          ),
        },
        {
          title: 'Position details',
          content: (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
              <DetailField
                icon={<BusinessIcon />}
                tooltip="Offices"
                value={positionFillRequest.officesLabel || EMPTY_VALUE_INDICATOR}
              />
              <DetailField
                icon={<CategoryOutlinedIcon />}
                tooltip="Category"
                value={
                  positionFillRequest.positionSlot
                    ? POSITION_SLOT_WORKER_TYPE_LABELS[positionFillRequest.positionSlot.workerType]
                    : EMPTY_VALUE_INDICATOR
                }
              />
              <DetailField
                icon={<PercentIcon />}
                tooltip="FTE"
                value={
                  positionFillRequest.positionSlot
                    ? EMPLOYEE_CONTRACT_ASSIGMENT_TYPES[positionFillRequest.positionSlot.workRateType]
                    : EMPTY_VALUE_INDICATOR
                }
              />

              <DetailField
                icon={<AccountTreeOutlinedIcon />}
                tooltip="Business function"
                value={positionFillRequest.businessFunctionLabel || EMPTY_VALUE_INDICATOR}
                sx={{ alignItems: 'flex-start', width: '100%' }}
              />

              <DetailField
                icon={<DescriptionOutlinedIcon />}
                tooltip="Position"
                value={<Box sx={{ display: 'block', width: '100%' }}>{position}</Box>}
                sx={{ gridColumn: '2 / span 2', alignItems: 'flex-start', width: '100%', minHeight: '100%' }}
              />
            </Box>
          ),
        },
        {
          title: 'Responsibilities',
          content: (
            <Box sx={htmlContentSx}>
              <SanitizedHtml html={positionFillRequest.responsibilities} />
            </Box>
          ),
        },
        {
          title: 'Requirements',
          content: (
            <Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
                <DetailField
                  icon={<AppIconLabel icon="outlinedEnglish" sxBox={{ mr: 0 }} />}
                  tooltip="English level"
                  value={ENGLISH_LEVEL_SHORT_OPTIONS[positionFillRequest.englishLevel]}
                />
                <DetailField
                  icon={<WorkIcon />}
                  tooltip="Required experience"
                  value={positionFillRequest.requiredExperience}
                />
              </Box>
              <Box sx={htmlContentSx}>
                <SanitizedHtml html={positionFillRequest.requirements} />
              </Box>
            </Stack>
          ),
        },
        {
          title: 'Project information',
          content:
            positionFillRequest.projectName || positionFillRequest.projectDescription ? (
              <Stack>
                {positionFillRequest.projectName ? (
                  <DetailField icon={<FolderIcon />} tooltip="Project name" value={positionFillRequest.projectName} />
                ) : null}
                {positionFillRequest.projectDescription ? (
                  <Box sx={htmlContentSx}>
                    <SanitizedHtml html={positionFillRequest.projectDescription} />
                  </Box>
                ) : null}
              </Stack>
            ) : (
              <Typography>{EMPTY_VALUE_INDICATOR}</Typography>
            ),
        },
        {
          title: 'Note',
          content: positionFillRequest.notes ? (
            <Box sx={htmlContentSx}>
              <SanitizedHtml html={positionFillRequest.notes} />
            </Box>
          ) : (
            <Typography>{EMPTY_VALUE_INDICATOR}</Typography>
          ),
        },
      ]}
    />
  );
};

export default PositionFillRequestDetailsCard;
